// Custom-auth token store. The backend issues its own JWTs via
// /api/v1/auth/local/login (and refreshes them via /api/v1/auth/refresh).
// These are stored here in localStorage so the app can authenticate even when
// Supabase is unavailable. Supabase is treated as a secondary/optional provider.

export interface CustomAuthTokens {
  access_token: string;
  refresh_token: string;
  user?: Record<string, unknown>;
}

const STORAGE_KEY = "uni-ui-auth-tokens";

export function loadAuthTokens(): CustomAuthTokens | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CustomAuthTokens;
    if (!parsed.access_token) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function saveAuthTokens(tokens: CustomAuthTokens): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(tokens));
  } catch {
    // ignore quota / privacy-mode errors
  }
}

export function clearAuthTokens(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

// Returns_the_bearer_token_to_send_on_API_requests, preferring the backend
// custom-auth JWT. Never throws — returns "" when unauthenticated.
export function getAuthBearerToken(): string {
  return loadAuthTokens()?.access_token || "";
}

// Decode the *payload* of a JWT without verifying the signature. Used only to
// derive UI state (user_id / email / name) from a token we already trust
// because it came from our own backend. Returns null if malformed.
export function decodeJwtPayload(
  token: string,
): Record<string, unknown> | null {
  try {
    const parts = token.split(".");
    if (parts.length < 2) return null;
    const b64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const json =
      typeof atob !== "undefined"
        ? atob(b64)
        : Buffer.from(b64, "base64").toString("utf-8");
    return JSON.parse(json);
  } catch {
    return null;
  }
}
